// Zweck: Getrennte Testdaten und simulierte Gegenstelle für den geführten Test.
// Kontext: Ausschließlich Testmodus; kein Aufruf von HERO oder 360. Stand: 20.10.2026.
import localforage from 'localforage';
import type {
  WarehouseSnapshot,
  WarehouseTransport,
  WarehouseBooking,
  WarehouseResult,
} from '../interfaces/warehouse';
const db = localforage.createInstance({
  name: '360volt-warehouse-demo',
  storeName: 'server',
});
export const demoSnapshot = (): WarehouseSnapshot => ({
  asOf: new Date().toISOString(),
  canManage: true,
  articles: [
    {
      id: '12345',
      number: '12345',
      name: 'Kupplung 22 mm',
      unit: 'Stück',
      heroId: 'demo-1',
      codes: ['12345'],
      fractional: false,
    },
    {
      id: '67890',
      number: '67890',
      name: 'Kupferrohr 22 mm',
      unit: 'Meter',
      heroId: 'demo-2',
      codes: ['67890'],
      fractional: true,
    },
    {
      id: '24680',
      number: '24680',
      name: 'Dichtung ¾ Zoll',
      unit: 'Stück',
      heroId: 'demo-3',
      codes: ['24680'],
      fractional: false,
    },
    {
      id: '31001',
      number: '31001',
      name: 'Rohrschelle 22 mm',
      unit: 'Stück',
      heroId: 'demo-4',
      codes: ['31001'],
      fractional: false,
    },
  ],
  locations: [
    { id: 'central', name: 'Zentrallager' },
    { id: 'secondary', name: 'Nebenlager' },
  ],
  stocks: [
    ...[
      ['12345', 12],
      ['67890', 24.5],
      ['24680', 30],
      ['31001', 40],
    ].map(([articleId, quantity]) => ({
      articleId: String(articleId),
      locationId: 'central',
      quantity: Number(quantity),
      shelf: 'Regal A',
    })),
    ...['12345', '67890', '24680', '31001'].map((articleId) => ({
      articleId,
      locationId: 'secondary',
      quantity: articleId === '12345' ? 3 : 0,
      shelf: 'Regal N',
    })),
  ],
  projects: [
    { id: 'P-101', name: 'Wärmepumpe Müller' },
    { id: 'P-102', name: 'Wärmepumpe Schneider' },
    { id: 'P-103', name: 'Wärmepumpe Müller' },
    ...Array.from({ length: 200 }, (_, i) => ({
      id: `DEMO-${1000 + i}`,
      name: `Wärmepumpe Musterkunde ${i + 1}`,
    })),
  ],
  lists: [
    {
      id: 'material-mueller-1',
      projectId: 'P-101',
      version: 1,
      items: [
        { articleId: '12345', planned: 10, issued: 0 },
        { articleId: '67890', planned: 15, issued: 0 },
        { articleId: '31001', planned: 20, issued: 0 },
      ],
    },
  ],
  bookings: [],
  heroSync: [],
});
export const readDemo = async () =>
  (await db.getItem<WarehouseSnapshot>('snapshot')) ?? demoSnapshot();
export async function resetDemo() {
  const s = demoSnapshot();
  await db.setItem('snapshot', s);
  return s;
}
export async function demoConflict() {
  const s = await readDemo();
  s.stocks.find(
    (x) => x.articleId === '12345' && x.locationId === 'central',
  )!.quantity = 1;
  await db.setItem('snapshot', s);
}
export function demoTransport(loseResponse: () => boolean): WarehouseTransport {
  return {
    snapshot: readDemo,
    status: async (id) =>
      (await readDemo()).bookings.find((x) => x.requestId === id),
    book: async (booking: WarehouseBooking) => {
      const s = await readDemo();
      const existing = s.bookings.find(
        (x) => x.requestId === booking.requestId,
      );
      if (existing) {
        if (JSON.stringify(existing.booking) !== JSON.stringify(booking))
          throw new Error('Vorgangs-ID mit anderem Inhalt.');
        return existing;
      }
      const original = s.bookings.find(
        (x) => x.requestId === booking.correctsId,
      )?.booking;
      const sign = (add = false) =>
        booking.kind === 'issue'
          ? -1
          : booking.kind === 'correction'
            ? (original?.kind === 'issue' ? -1 : 1) * (add ? 1 : -1)
            : 1;
      const list = s.lists.find(
        (x) =>
          x.id ===
          (booking.kind === 'correction' ? original?.listId : booking.listId),
      );
      const corrections = s.bookings.filter(
        (x) =>
          x.status === 'confirmed' &&
          x.booking.kind === 'correction' &&
          x.booking.correctsId === booking.correctsId,
      );
      const invalidCorrection =
        booking.kind === 'correction' &&
        (!original ||
          !['receipt', 'issue', 'return'].includes(original.kind) ||
          booking.lines.some(
            (l) =>
              !original.lines.some((o) => o.articleId === l.articleId) ||
              (!l.addInsteadOfReverse &&
                l.quantity >
                  (original.lines.find((o) => o.articleId === l.articleId)
                    ?.quantity ?? 0) +
                    corrections
                      .flatMap((c) => c.booking.lines)
                      .filter((x) => x.articleId === l.articleId)
                      .reduce(
                        (sum, x) =>
                          sum +
                          (x.addInsteadOfReverse ? x.quantity : -x.quantity),
                        0,
                      )),
          ));
      const error = invalidCorrection
        ? 'reference'
        : list &&
            booking.kind !== 'correction' &&
            list.version !== booking.listVersion
          ? 'list'
          : booking.lines.some(
                (l) =>
                  (s.stocks.find(
                    (x) =>
                      x.articleId === l.articleId &&
                      x.locationId === booking.locationId,
                  )?.quantity ?? 0) +
                    sign(l.addInsteadOfReverse) * l.quantity <
                    0 && !l.negativeReason,
              )
            ? 'stock'
            : null;
      const result: WarehouseResult = {
        requestId: booking.requestId,
        status: error ? 'rejected' : 'confirmed',
        code: error,
        message:
          error === 'reference'
            ? 'Nicht mehr zurücknehmen als ursprünglich und durch Korrekturen gebucht.'
            : error === 'stock'
              ? 'Bestand reicht nicht aus. Menge oder Begründung prüfen.'
              : error
                ? 'Materialliste wurde geändert. Aktuellen Stand prüfen.'
                : null,
        booking: structuredClone(booking),
        actorId: 1,
        createdAt: new Date().toISOString(),
      };
      if (!error) {
        for (const l of booking.lines) {
          s.stocks.find(
            (x) =>
              x.articleId === l.articleId &&
              x.locationId === booking.locationId,
          )!.quantity =
            Math.round(
              (s.stocks.find(
                (x) =>
                  x.articleId === l.articleId &&
                  x.locationId === booking.locationId,
              )!.quantity +
                sign(l.addInsteadOfReverse) * l.quantity) *
                1000,
            ) / 1000;
          const item = list?.items.find((x) => x.articleId === l.articleId);
          if (item)
            item.issued = Math.max(
              0,
              item.issued +
                (booking.kind === 'correction' && !l.addInsteadOfReverse
                  ? -l.quantity
                  : l.quantity),
            );
        }
        if (list) list.version++;
      }
      s.bookings.unshift(result);
      s.asOf = new Date().toISOString();
      await db.setItem('snapshot', s);
      if (loseResponse())
        throw new Error(
          'Test: Antwort verloren. Status prüfen, nicht neu buchen.',
        );
      return result;
    },
  };
}
